import { useState, useContext } from "react";
import {
  Role,
  useGetMyTeamMembersQuery,
  useInviteMyTeamMemberMutation,
  useRemoveMyTeamMemberMutation,
  useSetMyTeamMemberTemporaryPasswordMutation,
} from "../../services/api/__generated__/backend_gateway-types";
import { SysMessageLevel } from "../../contexts/sys_messages/type";
import { SysMessagesContext } from "../../contexts/sys_messages/context";
import { useApi } from "../../contexts/app/provider";
import { MyTeamComponent } from "./component";

export const MyTeamContainer = () => {
  const { apiService } = useApi();
  const { addSystemMessage } = useContext(SysMessagesContext);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [removeConfirmModalOpen, setRemoveConfirmModalOpen] = useState(false);
  const [userToRemove, setUserToRemove] = useState<string | null>(null);

  // GraphQL queries and mutations
  const {
    data: teamMembersData,
    loading: teamMembersLoading,
    error: teamMembersError,
    refetch: refetchTeamMembers,
  } = useGetMyTeamMembersQuery({
    client: apiService.getClient(),
    onError: (error) => {
      addSystemMessage({
        level: SysMessageLevel.DANGER,
        message: `Failed to load team members: ${error.message}`,
      });
    },
  });

  const [inviteTeamMember, { loading: inviteLoading }] =
    useInviteMyTeamMemberMutation({
      client: apiService.getClient(),
      onCompleted: () => {
        addSystemMessage({
          level: SysMessageLevel.SUCCESS,
          message: "Team member invited successfully",
        });
        setInviteModalOpen(false);
        refetchTeamMembers();
      },
      onError: (error) => {
        addSystemMessage({
          level: SysMessageLevel.DANGER,
          message: `Failed to invite team member: ${error.message}`,
        });
      },
    });

  const [removeTeamMember, { loading: removeLoading }] =
    useRemoveMyTeamMemberMutation({
      client: apiService.getClient(),
      onCompleted: () => {
        addSystemMessage({
          level: SysMessageLevel.SUCCESS,
          message: "Team member removed successfully",
        });
        setRemoveConfirmModalOpen(false);
        setUserToRemove(null);
        refetchTeamMembers();
      },
      onError: (error) => {
        addSystemMessage({
          level: SysMessageLevel.DANGER,
          message: `Failed to remove team member: ${error.message}`,
        });
      },
    });

  const [setTemporaryPassword, { loading: passwordLoading }] =
    useSetMyTeamMemberTemporaryPasswordMutation({
      client: apiService.getClient(),
      onCompleted: () => {
        addSystemMessage({
          level: SysMessageLevel.SUCCESS,
          message: "Password updated successfully",
        });
        refetchTeamMembers();
      },
      onError: (error) => {
        addSystemMessage({
          level: SysMessageLevel.DANGER,
          message: `Failed to update password: ${error.message}`,
        });
      },
    });

  // Event handlers
  const handleInviteTeamMember = async (
    email: string,
    role: Role,
    temporaryPassword?: string
  ) => {
    await inviteTeamMember({
      variables: {
        email,
        role,
        temporaryPassword: temporaryPassword || undefined,
      },
    });
  };

  const handleRemoveTeamMember = async (email: string) => {
    setUserToRemove(email);
    setRemoveConfirmModalOpen(true);
  };

  const confirmRemoveTeamMember = async () => {
    if (userToRemove) {
      await removeTeamMember({
        variables: {
          email: userToRemove,
        },
      });
    }
  };

  const handleSetPassword = async (
    email: string,
    temporaryPassword: string
  ) => {
    await setTemporaryPassword({
      variables: {
        email,
        temporaryPassword,
      },
    });
  };

  const handleOpenInviteModal = () => {
    setInviteModalOpen(true);
  };

  const handleCloseInviteModal = () => {
    setInviteModalOpen(false);
  };

  const handleCloseRemoveConfirmModal = () => {
    setRemoveConfirmModalOpen(false);
    setUserToRemove(null);
  };

  return (
    <MyTeamComponent
      teamMembers={teamMembersData?.getMyTeamMembers || []}
      loading={teamMembersLoading}
      error={teamMembersError}
      inviteModalOpen={inviteModalOpen}
      removeConfirmModalOpen={removeConfirmModalOpen}
      userToRemove={userToRemove}
      inviteLoading={inviteLoading}
      removeLoading={removeLoading}
      passwordLoading={passwordLoading}
      onInviteTeamMember={handleInviteTeamMember}
      onRemoveTeamMember={handleRemoveTeamMember}
      onConfirmRemoveTeamMember={confirmRemoveTeamMember}
      onSetPassword={handleSetPassword}
      onOpenInviteModal={handleOpenInviteModal}
      onCloseInviteModal={handleCloseInviteModal}
      onCloseRemoveConfirmModal={handleCloseRemoveConfirmModal}
    />
  );
};
